/* DillySocks — Warenkorb-Upsell: Panels, Lazy-Load, Add-to-Cart, Tracking
   (Sean 01.09.2026, Briefing "Warenkorb-Upsell-System").

   Architektur:
   - Banner (ds-goodie-bar) und Panel-Leiste (ds-cart-panels) sind LIQUID im
     Drawer-Snippet — Horizon rendert den Drawer bei jeder Warenkorbaenderung
     per Section Rendering API neu, damit sind sie immer aktuell.
   - Die Panel-INHALTE laden erst beim Oeffnen: fetch auf
     {root}?sections=ds-cart-upsell bzw. ds-cart-gift (Section Rendering API,
     serverseitige Kandidaten-/Preislogik, Money-Filter — nichts clientseitig
     gerechnet).
   - Add-to-Cart nutzt Horizons CartLinesUpdateEvent (wie ds-quick-add.js),
     damit der Drawer sich selbst aktualisiert; danach werden Panelzustand
     und -inhalt wiederhergestellt (der Re-Render ersetzt das Drawer-DOM).
   - Tracking: Shopify Customer Events via Shopify.analytics.publish —
     cart_goodie_threshold_reached, upsell_panel_open, upsell_add,
     gift_panel_open, gift_add.

   Alle Handler sind delegiert (document-Ebene) und ueberleben Re-Renders. */
import { CartLinesUpdateEvent } from '@shopify/events';

(function () {
  'use strict';

  var MARKET = window.DS_MARKET || {};
  var ROOT = MARKET.root || '';
  var openPanel = null; // 'upsell' | 'gift' | null — ueberlebt Re-Renders
  var cache = {}; // Panel-HTML je Typ (wird nach Cart-Aenderung verworfen)
  var lastReached = null; // Stufenzaehler fuer threshold_reached-Event
  var lastPct = null; // Balkenbreite fuer sanfte Animation nach Re-Render

  /* A/B-Grundgeruest (Sean 01.09.): Variante wird einmal pro Browser
     zufaellig zugeteilt und an JEDES Event gehaengt — damit sind spaetere
     Angebots-Tests sofort auswertbar (GA4: Segment nach ab_variant).
     Welche Variante was zeigt, entscheidet spaeter Liquid/Settings. */
  function abVariant() {
    try {
      var v = localStorage.getItem('ds_ab_variant');
      if (!v) {
        v = Math.random() < 0.5 ? 'a' : 'b';
        localStorage.setItem('ds_ab_variant', v);
      }
      return v;
    } catch (e) { return 'a'; }
  }

  function track(name, payload) {
    payload = Object.assign({ ab_variant: abVariant() }, payload || {});
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish(name, payload || {});
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, payload || {}));
    } catch (e) { /* Tracking darf nie den Kauf brechen */ }
  }

  /* ---- Goodie-Stufen: Erreichen einer Stufe erkennen -------------------- */
  function watchThresholds() {
    var bar = document.querySelector('[data-ds-goodie]');
    if (!bar) return;
    var reached = parseInt(bar.getAttribute('data-ds-goodie-reached') || '0', 10);
    if (lastReached !== null && reached > lastReached) {
      track('cart_goodie_threshold_reached', { stufe: reached });
    }
    lastReached = reached;

    /* Sanfte Balken-Animation (Sean 01.09. Punkt 6): Der Re-Render ersetzt
       das Fill-Element, die CSS-Transition greift dann nicht. Deshalb:
       neuen Zielwert lesen, Element auf den ALTEN Wert zuruecksetzen und
       im naechsten Frame zum Ziel uebergehen. */
    var fill = bar.querySelector('.ds-freebar__fill');
    if (fill) {
      var target = parseFloat(fill.style.width) || 0;
      if (lastPct !== null && Math.abs(lastPct - target) > 0.5) {
        fill.style.transition = 'none';
        fill.style.width = lastPct + '%';
        void fill.offsetWidth;
        fill.style.transition = '';
        fill.style.width = target + '%';
      }
      lastPct = target;
    }
  }

  /* ---- Panels ----------------------------------------------------------- */
  function panelEl(type) {
    return document.querySelector('[data-ds-panel="' + type + '"]');
  }

  function setExpanded(type, expanded) {
    var btn = document.querySelector('[data-ds-panel-toggle="' + type + '"]');
    if (btn) btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    var el = panelEl(type);
    if (el) {
      if (expanded) {
        el.hidden = false;
        /* Hoehenanimation ohne CLS: grid-rows 0fr -> 1fr (CSS). Frame-
           Wechsel ueber Reflow + setTimeout statt rAF — rAF friert in
           Hintergrund-Tabs ein und das Panel bliebe unsichtbar. */
        void el.offsetHeight;
        setTimeout(function () { el.classList.add('is-open'); }, 20);
      } else {
        el.classList.remove('is-open');
        setTimeout(function () { el.hidden = true; }, 260);
      }
    }
  }

  async function loadPanel(type) {
    var el = panelEl(type);
    if (!el) return;
    var body = el.querySelector('[data-ds-panel-body]');
    if (!body) return;
    if (cache[type]) {
      body.innerHTML = cache[type];
      setTimeout(fadeAllRows, 30);
      return;
    }
    var section = type === 'upsell' ? 'ds-cart-upsell' : 'ds-cart-gift';
    try {
      var res = await fetch(ROOT + '/?sections=' + section);
      var data = await res.json();
      var html = data[section] || '';
      /* Section-Wrapper abstreifen */
      var tmp = document.createElement('div');
      tmp.innerHTML = html;
      var inner = tmp.querySelector('[data-ds-upsell-content], [data-ds-gift-content]');
      cache[type] = inner ? inner.outerHTML : html;
      body.innerHTML = cache[type];
    } catch (e) {
      body.innerHTML = '';
    }
    setTimeout(fadeAllRows, 30);
  }

  function toggle(type) {
    if (openPanel === type) {
      openPanel = null;
      setExpanded(type, false);
      return;
    }
    if (openPanel) setExpanded(openPanel, false);
    openPanel = type;
    setExpanded(type, true);
    loadPanel(type);
    track(type === 'upsell' ? 'upsell_panel_open' : 'gift_panel_open', {});
  }

  /* Nach jedem Drawer-Re-Render: Banner-Stufen pruefen und offenen Panel-
     Zustand wiederherstellen (Inhalt neu laden — Restbetrag/Badges haben
     sich geaendert). */
  function restore() {
    watchThresholds();
    if (openPanel) {
      var el = panelEl(openPanel);
      if (el && el.hidden) {
        cache = {};
        setExpanded(openPanel, true);
        loadPanel(openPanel);
      } else if (!el) {
        /* Panel existiert nicht mehr (z.B. Schwelle erreicht -> Auffuell-
           Button weg) — Zustand aufraeumen. */
        openPanel = null;
      }
    }
  }

  /* ---- Scroll-Affordance der Groessen-Zeilen ----------------------------
     Liegen rechts (oder links) weitere Groessen, faded die Maske in
     ds-cart.css die Randpille aus. Klassen hier pflegen: einmal nach dem
     Rendern, danach pro Scroll-Event (capture — scroll bubbelt nicht). */
  function fadeRow(row) {
    var max = row.scrollWidth - row.clientWidth;
    row.classList.toggle('has-more-end', max > 4 && row.scrollLeft < max - 4);
    row.classList.toggle('has-more-start', row.scrollLeft > 4);
  }
  function fadeAllRows() {
    document.querySelectorAll('[data-ds-upcard] .ds-upcard__sizes').forEach(fadeRow);
  }
  document.addEventListener('scroll', function (e) {
    var t = e.target;
    if (t && t.classList && t.classList.contains('ds-upcard__sizes')) fadeRow(t);
  }, true);

  /* ---- Add-to-Cart (Karten + Gutschein-Chips) --------------------------- */
  var busy = false;
  async function addVariant(variantId, meta) {
    if (busy) return false;
    busy = true;
    var deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart-upsell',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    }));
    try {
      /* _ds_upsell-Property (Unterstrich = im Checkout unsichtbar) macht
         jede Upsell-Position in Bestellungen und Reports auswertbar:
         Quelle (refill/gift_wrap/gift_card) + A/B-Variante. */
      var props = { _ds_upsell: (meta && meta.source) || 'refill', _ds_ab: abVariant() };
      var res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1, properties: props }] }),
      });
      if (!res.ok) throw new Error('add failed');
      var ajaxCart = await (await fetch('/cart.js')).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: { items: ajaxCart.items, itemCount: ajaxCart.item_count, didError: false },
      });
      track(meta.event, meta.payload);
      busy = false;
      return true;
    } catch (e) {
      deferred.resolve({ detail: { didError: true } });
      busy = false;
      return false;
    }
  }

  /* ---- Delegierte Events ------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var t;

    t = e.target.closest('[data-ds-panel-toggle]');
    if (t) {
      e.preventDefault();
      toggle(t.getAttribute('data-ds-panel-toggle'));
      return;
    }

    t = e.target.closest('[data-ds-upcard-size]');
    if (t && !t.disabled) {
      var card = t.closest('[data-ds-upcard]');
      card.querySelectorAll('[data-ds-upcard-size]').forEach(function (b) {
        b.setAttribute('aria-pressed', 'false');
      });
      t.setAttribute('aria-pressed', 'true');
      var add = card.querySelector('[data-ds-upcard-add]');
      if (add) {
        add.disabled = false;
        add.setAttribute('data-variant-id', t.getAttribute('data-ds-upcard-size'));
      }
      return;
    }

    t = e.target.closest('[data-ds-upcard-add]');
    if (t && !t.disabled) {
      var card2 = t.closest('[data-ds-upcard]');
      var vid = t.getAttribute('data-variant-id');
      if (!vid) return;
      var hasBadge = !!card2.querySelector('.ds-upcard__badge');
      var inGift = !!t.closest('[data-ds-gift-content]');
      var src = inGift ? 'gift_wrap' : card2.getAttribute('data-ds-source') || 'refill';
      t.disabled = true;
      addVariant(vid, {
        source: src,
        event: inGift ? 'gift_add' : 'upsell_add',
        payload: inGift
          ? { typ: 'verpackung', produkt: card2.getAttribute('data-product-id') }
          : { produkt: card2.getAttribute('data-product-id'), hat_badge: hasBadge, quelle: src },
      }).then(function (ok) {
        if (ok) {
          /* Karte verschwindet; Bestaetigung inline. Banner/Panel erneuert
             der Drawer-Re-Render + restore(). */
          var done = card2.parentElement.parentElement.querySelector('[data-ds-upsell-done]');
          card2.remove();
          if (done) done.hidden = false;
          cache = {};
        } else {
          t.disabled = false;
        }
      });
      return;
    }

    t = e.target.closest('[data-ds-gift-card]');
    if (t) {
      t.disabled = true;
      addVariant(t.getAttribute('data-ds-gift-card'), {
        source: 'gift_card',
        event: 'gift_add',
        payload: { typ: 'gutschein' },
      }).then(function (ok) { if (!ok) t.disabled = false; });
      return;
    }

    t = e.target.closest('[data-ds-wrap-filter]');
    if (t) {
      var grid = document.querySelector('[data-ds-wrap-grid]');
      if (grid) {
        var group = t.getAttribute('data-ds-wrap-filter');
        var wasActive = t.getAttribute('aria-pressed') === 'true';
        document.querySelectorAll('[data-ds-wrap-filter]').forEach(function (b) {
          b.setAttribute('aria-pressed', 'false');
        });
        grid.querySelectorAll('[data-ds-wrap-card]').forEach(function (c) {
          if (wasActive) {
            /* Filter abgewaehlt -> zurueck zur kuratierten Default-Auswahl */
            c.hidden = !c.hasAttribute('data-default');
          } else {
            c.hidden = c.getAttribute('data-group') !== group;
          }
        });
        if (!wasActive) t.setAttribute('aria-pressed', 'true');
      }
      return;
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openPanel) {
      var type = openPanel;
      openPanel = null;
      setExpanded(type, false);
      var btn = document.querySelector('[data-ds-panel-toggle="' + type + '"]');
      if (btn) btn.focus();
    }
  });

  /* ---- Drawer-Re-Render beobachten (Muster wie ds-cart.js) -------------- */
  function init() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    watchThresholds();
    var scheduled = false;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      setTimeout(function () {
        scheduled = false;
        restore();
      }, 120);
    }).observe(drawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
